import React from 'react';
import { REPORT_DEFINITIONS, REPORT_CATEGORIES } from './reportDefinitions';
import {
  BanknotesIcon,
  ChartPieIcon,
  ChartBarIcon,
  BriefcaseIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  UsersIcon,
  DocumentTextIcon,
  UserGroupIcon,
  MegaphoneIcon,
  XCircleIcon,
  QueueListIcon,
  PresentationChartLineIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

// Icon by report id — falls back to DocumentTextIcon for anything not listed.
const REPORT_ICONS = {
  revenue: BanknotesIcon,
  margin_analysis: ChartPieIcon,
  margin_trend: PresentationChartLineIcon,
  job_performance: BriefcaseIcon,
  job_status: QueueListIcon,
  inventory: CubeIcon,
  payroll_summary: UsersIcon,
  bid_log: ClipboardDocumentListIcon,
  quote_performance: ChartBarIcon,
  customer_quote_contact: UserGroupIcon,
  quotes_by_lead_source: MegaphoneIcon,
  lost_quotes: XCircleIcon,
  email_log: EnvelopeIcon,
  sms_log: ChatBubbleLeftRightIcon,
};

const iconFor = (id) => REPORT_ICONS[id] || DocumentTextIcon;

// LAT-24: Coming soon reports
const COMING_SOON_REPORTS = [
  { id: 'email_log', title: 'Email Log', description: 'Track all emails sent to customers including opens and clicks.', category: 'communications' },
  { id: 'sms_log', title: 'SMS Log', description: 'Track all SMS messages sent to customers.', category: 'communications' },
];

export default function ReportsLibrary({ onSelectReport, userRole = 'admin' }) {
  return (
    <div className="space-y-6">
      {REPORT_CATEGORIES.map(category => {
        // LAT-25: Filter reports by role visibility
        const visibleReports = category.reports.filter(reportId => {
          const report = REPORT_DEFINITIONS[reportId];
          if (!report) return false;
          if (report.visibility === 'admin' && userRole !== 'admin' && userRole !== 'owner') return false;
          return true;
        });
        if (visibleReports.length === 0) return null;
        return (
        <div key={category.id}>
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            {category.label}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleReports.map(reportId => {
              const report = REPORT_DEFINITIONS[reportId];
              if (!report) return null;
              const Icon = iconFor(report.id);
              return (
                <div
                  key={report.id}
                  className="card p-4 cursor-pointer"
                  onClick={() => onSelectReport(report)}
                  onKeyDown={e => { if (e.key === 'Enter') onSelectReport(report); }}
                  tabIndex={0}
                  role="button"
                  aria-label={`View ${report.title} report`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 'var(--card-radius)',
                        background: 'var(--neutral-100)',
                        color: 'var(--color-action-primary)',
                      }}
                      aria-hidden="true"
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <h4 className="font-semibold flex-1 min-w-0" style={{ color: 'var(--color-text-primary)' }}>
                      {report.title}
                    </h4>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {report.description}
                  </p>
                  <button
                    className="text-sm font-medium focus-ring rounded px-2 py-1 mt-3"
                    style={{ color: 'var(--color-action-primary)' }}
                    onClick={(e) => { e.stopPropagation(); onSelectReport(report); }}
                  >
                    View Report →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        );
      })}

      {/* LAT-24: Coming Soon section */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-secondary)' }}>
          Communications
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {COMING_SOON_REPORTS.map(report => {
            const Icon = iconFor(report.id);
            return (
              <div
                key={report.id}
                className="card p-4 opacity-60"
                aria-disabled="true"
              >
                <div className="flex items-start gap-3 mb-2">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 'var(--card-radius)',
                      background: 'var(--neutral-100)',
                      color: 'var(--color-text-muted)',
                    }}
                    aria-hidden="true"
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                    <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      {report.title}
                    </h4>
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--neutral-200)', color: 'var(--color-text-secondary)' }}
                    >
                      Coming Soon
                    </span>
                  </div>
                </div>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {report.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
